import type { ReactNode } from 'react';
import type { AssetKey } from '@/assets/manifest';
import { ResponsiveImage } from '@/components/media/ResponsiveImage';
import { cx } from '@/lib/cx';
import styles from './Brandmark.module.css';

export const BRAND_NAME = 'AΩGR';
export const BRAND_SPOKEN = 'ÁGORA';
export const BRAND_SCHOOL = 'Escuela Pleyadiana';
/** Nombre accesible unico del bloque de marca. Se usa en `alt` y `aria-label`. */
export const BRAND_ACCESSIBLE_NAME = `${BRAND_NAME} (${BRAND_SPOKEN}) · ${BRAND_SCHOOL}`;

/**
 * Que pieza del logo oficial se pinta.
 *  · `espiral`  — el EMBLEMA circular dorado solo. El de cabecera, pie y hero.
 *  · `lockup`   — espiral + AΩGR alado.
 *  · `completo` — espiral + AΩGR debajo.
 *  · `none`     — sin imagen, solo el lockup tipografico.
 */
export type BrandEmblem = 'none' | 'espiral' | 'lockup' | 'completo';

/**
 * Como se ordenan emblema y texto.
 *  · `inline`  — en fila. CABECERA.
 *  · `stacked` — emblema sobre texto en dos lineas, centrado. PIE.
 *  · `hero`    — emblema sobre UNA linea "AΩGR · Escuela Pleyadiana", centrado
 *                y en oro, como el bloque superior del mockup de Cancun.
 */
export type BrandLayout = 'inline' | 'stacked' | 'hero';

const EMBLEM_ASSET: Record<Exclude<BrandEmblem, 'none'>, AssetKey> = {
  espiral: 'marca/espiral',
  lockup: 'marca/lockup',
  completo: 'marca/completo',
};

/** El emblema nunca pasa de estas anchuras: mas seria descargar de mas. */
const EMBLEM_SIZES: Record<'sm' | 'md' | 'lg', string> = {
  sm: '40px',
  md: '56px',
  lg: '(min-width: 768px) 112px, 84px',
};

export interface BrandmarkProps {
  /**
   * Pieza del logo OFICIAL (ADR-002). Por defecto el emblema circular: es lo
   * que llevan los mockups junto al texto. `none` deja solo el lockup
   * tipografico, que es correcto pero NO es el logo de marca.
   */
  emblem?: BrandEmblem;
  /**
   * Escotilla de escape: un nodo propio en lugar del emblema del manifiesto.
   * Solo para casos que el manifiesto no cubra.
   */
  logo?: ReactNode;
  layout?: BrandLayout;
  size?: 'sm' | 'md' | 'lg';
  /** Oculta el texto y deja solo el emblema (el nombre accesible se mantiene). */
  logoOnly?: boolean;
  /** El emblema esta sobre el pliegue: se descarga sin esperar. */
  eager?: boolean;
  className?: string;
}

/**
 * Bloque de marca. UNICO sitio del proyecto donde se compone el nombre.
 *
 * ADR-002: se rotula **AΩGR** (omega griega) y **ÁGORA** donde el diseno pide el
 * nombre hablado. NUNCA el rotulo de cuatro letras sin omega — eso es un error
 * heredado de las demos, y hay una regla de ESLint que lo bloquea en `src/**`.
 *
 * El emblema sale del manifiesto de FORGE-BRAVO (`marca/*`), con alfa real
 * verificada. Antes lo tenia que inyectar la pagina, pero `SiteHeader` y
 * `SiteFooter` los monta `Page`: una pagina no tenia por donde inyectar nada y
 * las seis rutas salian sin logo. Ahora viene de serie y se puede quitar.
 *
 * LIMITE TECNICO CONOCIDO: ninguna de las tres familias self-hosted publica
 * subset griego, asi que U+03A9 no existe en ellas. La omega se compone con
 * `--az-font-omega` (serif del sistema). Verificado con fontTools; documentado
 * en `src/assets/fonts/PROCEDENCIA.md` y en docs/PAGE_CONTRACT.md.
 */
export function Brandmark({
  emblem = 'espiral',
  logo,
  layout = 'inline',
  size = 'md',
  logoOnly = false,
  eager = false,
  className,
}: BrandmarkProps) {
  const mark =
    logo ??
    (emblem === 'none' ? null : (
      <ResponsiveImage
        asset={EMBLEM_ASSET[emblem]}
        sizes={EMBLEM_SIZES[size]}
        decorative
        loading={eager ? 'eager' : 'lazy'}
        fetchPriority={eager ? 'high' : undefined}
      />
    ));

  return (
    <span
      className={cx(styles.brand, styles[layout], styles[`size-${size}`], className)}
      aria-label={BRAND_ACCESSIBLE_NAME}
      role="img"
    >
      {mark ? <span className={styles.logo}>{mark}</span> : null}
      <span
        className={cx(styles.words, logoOnly && 'az-visually-hidden')}
        aria-hidden={logoOnly ? undefined : true}
      >
        <span className={styles.wordmark}>
          A<span className={cx('az-omega', styles.omega)}>Ω</span>GR
        </span>
        {/* En `hero` el separador va en la misma linea, como el mockup de
            Cancun ("AΩGR - Escuela Pleyadiana"). Es adorno: no se lee. */}
        {layout === 'hero' ? (
          <span className={styles.dash} aria-hidden="true">
            ·
          </span>
        ) : null}
        <span className={styles.school}>{BRAND_SCHOOL}</span>
      </span>
    </span>
  );
}
